import { useField } from 'vee-validate';

/**
 * Interface representing the form field setters for address-related fields
 * @interface AddressFormFields
 */
interface AddressFormFields {
  setAddress: (value: string) => void;
  setCountry: (value: string) => void;
  setCity: (value: string) => void;
  setState: (value: string) => void;
  setZip: (value: string) => void;
  setLatitude: (value: number) => void;
  setLongitude: (value: number) => void;
}

/**
 * A composable that provides form field setters for address-related fields
 *
 * @returns {Object} An object containing the getAddressFormFieldSetters function
 * @property {Function} getAddressFormFieldSetters - Returns an object with methods to set address-related form fields
 *
 * @example
 * ```ts
 * const { getAddressFormFieldSetters } = useAddressAutocomplete();
 * const { setAddress, setCity, setState, setCountry, setZip, setLatitude, setLongitude } =
 *   getAddressFormFieldSetters();
 *
 * // Use the setters to update form fields
 * setAddress('123 Main St');
 * setCity('New York');
 * setState('NY');
 * setCountry('US');
 * setZip('10001');
 * setLatitude(40.7128);
 * setLongitude(-74.0060);
 * ```
 */
export default () => {
  /**
   * Creates and returns an object containing setter functions for address-related form fields
   *
   * @returns {AddressFormFields} An object containing setter functions for each address field
   */
  const getAddressFormFieldSetters = (): AddressFormFields => {
    const { setValue: setAddress } = useField<string>('address.address');
    const { setValue: setCountry } = useField<string>('address.country');
    const { setValue: setCity } = useField<string>('address.city');
    const { setValue: setState } = useField<string>('address.state');
    const { setValue: setZip } = useField<string>('address.zip');
    const { setValue: setLatitude } = useField<number>('address.coordinates.latitude');
    const { setValue: setLongitude } = useField<number>('address.coordinates.longitude');

    return { setAddress, setCountry, setCity, setState, setZip, setLatitude, setLongitude };
  };

  return { getAddressFormFieldSetters };
};
